import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LANG_LABELS, LANGS, type Lang } from '@/i18n';
import { switchLanguagePath, useLang } from '@/hooks/useLang';

/** Language switch: compact code pills in the header, full-name pill bar in the mobile menu. */
export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const lang = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const change = (next: Lang) => navigate(`${switchLanguagePath(location.pathname, next)}${location.search}${location.hash}`);

  return (
    <div
      role="group"
      aria-label={t('common.language')}
      className={compact ? 'flex items-center rounded-lg border border-ink-600 bg-ink-900/60 p-0.5' : 'flex items-center gap-1 rounded-2xl border border-white/15 bg-ink-950/70 p-1.5 shadow-[inset_0_1px_0_rgb(255_255_255/0.05)]'}
    >
      {LANGS.map((l) => {
        const active = l === lang;
        return (
          <button
            key={l}
            type="button"
            lang={l}
            onClick={() => change(l)}
            aria-pressed={active}
            title={LANG_LABELS[l]}
            className={
              compact
                ? `rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${active ? 'bg-volt-500 text-white' : 'text-steel-300 hover:text-white'}`
                : `flex-1 rounded-xl px-3 py-2.5 text-base font-semibold transition-colors ${active ? 'bg-gradient-to-b from-volt-400 to-volt-600 text-white shadow-[0_0_16px_-4px_rgb(46_139_255/0.9)]' : 'text-steel-200 hover:bg-white/5 hover:text-white'}`
            }
          >
            {compact ? l.toUpperCase() : LANG_LABELS[l]}
          </button>
        );
      })}
    </div>
  );
}
